import React, { useMemo, memo, useCallback } from 'react';
import { CalculationResults, PropertyInputs } from '../../types';
import { calculateSingleProperty, formatValue } from '../../utils';
import { useApp } from '../../contexts/AppContext';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface SensitivityAnalysisProps {
  calculations: CalculationResults;
}

const SensitivityAnalysis: React.FC<SensitivityAnalysisProps> = memo(() => {
  const { currentInputs } = useApp();
  const [isExpanded, setIsExpanded] = React.useState(true);

  // Calculate sensitivity for different parameters
  const calculateSensitivity = useCallback((baseInputs: PropertyInputs & any, parameter: string, steps: number[]) => {
    return steps.map(step => {
      const modifiedInputs = { ...baseInputs };
      
      // Apply the step change based on parameter type
      switch(parameter) {
        case 'kaufpreis':
          modifiedInputs.angebotspreis = baseInputs.angebotspreis * (1 + step/100);
          break;
        case 'zinssatz':
          modifiedInputs.zinssatz = baseInputs.zinssatz + step;
          break;
        case 'tilgung':
          modifiedInputs.tilgung = baseInputs.tilgung + step;
          break;
        case 'miete':
          modifiedInputs.kaltmieteProQm = baseInputs.kaltmieteProQm * (1 + step/100);
          break;
        case 'finanzierung':
          modifiedInputs.finanzierungsanteil = Math.max(0, Math.min(110, baseInputs.finanzierungsanteil + step));
          break;
        default:
          break;
      }
      
      const result = calculateSingleProperty(modifiedInputs);
      return result.monatlicherCashflowJahr1SollNachSteuer;
    });
  }, []);

  const sensitivityData = useMemo(() => {
    const steps = [-10, -5, 0, 5, 10]; // -2 steps, -1 step, basis, +1 step, +2 steps
    
    // For Jahr 1
    const jahr1Data = {
      kaufpreis: calculateSensitivity(currentInputs, 'kaufpreis', steps),
      zinssatz: calculateSensitivity(currentInputs, 'zinssatz', [-0.8, -0.4, 0, 0.4, 0.8]),
      tilgung: calculateSensitivity(currentInputs, 'tilgung', [0, 0, 0, 0, 0]), // Tilgung doesn't affect Jahr 1 cashflow
      miete: calculateSensitivity(currentInputs, 'miete', steps),
      finanzierung: calculateSensitivity(currentInputs, 'finanzierung', [-20, -10, 0, 10, 20])
    };

    // For Jahr 6 - we need to get year 6 cashflow from projection data
    const getYear6Cashflow = (inputs: PropertyInputs & any) => {
      const result = calculateSingleProperty(inputs);
      if (result.projectionData && result.projectionData.length > 6) {
        return result.projectionData[6].monatlicherCashflowNachSteuer || 0;
      }
      return 0;
    };

    const jahr6Data = {
      kaufpreis: steps.map(step => {
        const modifiedInputs = { ...currentInputs, angebotspreis: currentInputs.angebotspreis * (1 + step/100) };
        return getYear6Cashflow(modifiedInputs);
      }),
      zinssatz: [-0.8, -0.4, 0, 0.4, 0.8].map(step => {
        const modifiedInputs = { ...currentInputs, zinssatz: currentInputs.zinssatz + step };
        return getYear6Cashflow(modifiedInputs);
      }),
      tilgung: [0, 0, 0, 0, 0].map(() => getYear6Cashflow(currentInputs)), // Same for all
      miete: steps.map(step => {
        const modifiedInputs = { ...currentInputs, kaltmieteProQm: currentInputs.kaltmieteProQm * (1 + step/100) };
        return getYear6Cashflow(modifiedInputs);
      }),
      finanzierung: [-20, -10, 0, 10, 20].map(step => {
        const modifiedInputs = { ...currentInputs, finanzierungsanteil: Math.max(0, Math.min(110, currentInputs.finanzierungsanteil + step)) };
        return getYear6Cashflow(modifiedInputs);
      })
    };

    return { jahr1: jahr1Data, jahr6: jahr6Data };
  }, [currentInputs, calculateSensitivity]);

  const getCellColor = useCallback((value: number, baseValue: number) => {
    if (Math.abs(value - baseValue) < 0.01) return 'bg-yellow-100 border-2 border-blue-600 font-bold'; // Base value
    if (value > baseValue + 50) return 'bg-green-200';
    if (value > baseValue) return 'bg-green-100';
    if (value < baseValue - 50) return 'bg-red-200';
    if (value < baseValue) return 'bg-red-100';
    return 'bg-orange-100';
  }, []);

  const renderTable = useCallback((data: any, title: string) => {
    const baseValue = data.kaufpreis[2]; // Middle value is the base
    
    return (
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4">{title}</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border-b">Parameter</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 border-b">-2 Schritte</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 border-b">-1 Schritt</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 border-b">Basis</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 border-b">+1 Schritt</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 border-b">+2 Schritte</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-700 border-b">Kaufpreis SOLL</td>
                {data.kaufpreis.map((value: number, index: number) => (
                  <td key={index} className={`px-4 py-3 text-center text-sm border-b ${getCellColor(value, baseValue)}`}>
                    {formatValue(value, '€')}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-700 border-b">Zinssatz</td>
                {data.zinssatz.map((value: number, index: number) => (
                  <td key={index} className={`px-4 py-3 text-center text-sm border-b ${getCellColor(value, baseValue)}`}>
                    {formatValue(value, '€')}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-700 border-b">Tilgung</td>
                {data.tilgung.map((value: number, index: number) => (
                  <td key={index} className={`px-4 py-3 text-center text-sm border-b ${getCellColor(value, baseValue)}`}>
                    {formatValue(value, '€')}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-700 border-b">Miete SOLL</td>
                {data.miete.map((value: number, index: number) => (
                  <td key={index} className={`px-4 py-3 text-center text-sm border-b ${getCellColor(value, baseValue)}`}>
                    {formatValue(value, '€')}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-700">Finanzierungsanteil</td>
                {data.finanzierung.map((value: number, index: number) => (
                  <td key={index} className={`px-4 py-3 text-center text-sm ${getCellColor(value, baseValue)}`}>
                    {formatValue(value, '€')}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }, [getCellColor]);

  return (
    <div className="mt-8 bg-white p-6 rounded-lg shadow-md">
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h2 className="text-xl font-bold flex items-center gap-2">
          <span className="text-2xl">📊</span>
          Sensitivitätsanalyse
        </h2>
        {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
      </div>
      
      {isExpanded && (
        <div className="mt-6">
          <p className="text-sm text-gray-600 mb-6">
            Die Sensitivitätsanalyse zeigt, wie sich der monatliche Cashflow bei Änderung verschiedener Parameter entwickelt.
            Der Basiswert ist blau umrandet. Grüne Felder zeigen positive, rote Felder negative Auswirkungen.
          </p>
          
          {renderTable(sensitivityData.jahr1, 'Monatlicher Cashflow v.St. (Jahr 1)')}
          {renderTable(sensitivityData.jahr6, 'Monatlicher Cashflow v.St. (Jahr 6)')}
          
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">Erläuterung der Schritte:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• <strong>Kaufpreis:</strong> -10%, -5%, Basis, +5%, +10%</li>
              <li>• <strong>Zinssatz:</strong> -0.8%, -0.4%, Basis, +0.4%, +0.8%</li>
              <li>• <strong>Tilgung:</strong> Keine Änderung (beeinflusst Jahr 1 Cashflow nicht direkt)</li>
              <li>• <strong>Miete:</strong> -10%, -5%, Basis, +5%, +10%</li>
              <li>• <strong>Finanzierungsanteil:</strong> -20%, -10%, Basis, +10%, +20%</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
});

SensitivityAnalysis.displayName = 'SensitivityAnalysis';

export default SensitivityAnalysis;